import { GADataProps } from "@elements/GAData/GAData";
import {
  getAuthorsName,
  getChapter1,
  getContentCat,
  getLevel2,
  getTagsName,
} from "@elements/GAData/GADataHelper";
import { MetaTagsProps } from "@elements/MetaTags/MetaTags";
import { getArticleJSONLD } from "@helper/jsonLDHelper";
import { capitalize } from "@helper/stringTransform";
import { dateFormatWithTz, formatDate } from "@helper/useDateFormat";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { ProcessedArticleBody, RawImageField } from "@typings/Opensearch";
import { PageMetaDataValues } from "@typings/PageMetaData";

type ArticleMetaDataProps = {
  article: ProcessedArticleData;
  pageNumber: number;
  gsChannels: string[];
};

export default function ArticleMetaData({
  article,
  pageNumber,
  gsChannels,
}: ArticleMetaDataProps): PageMetaDataValues {
  const keywords = getTagsName(article.tags || []);
  const image = {
    url: article.imageField?.url,
    width: 480,
    height: 270,
  } as RawImageField;
  const ogImage = {
    url: article.ogImageUrl || article.imageField?.url,
    width: 480,
    height: 270,
  } as RawImageField;
  const level2 = getLevel2(article);
  const metaTitle =
    article.seoFields?.metaTitle && article.seoFields.metaTitle !== ""
      ? article.seoFields.metaTitle
      : `${article.title}, Berita ${capitalize(level2)} - Beritaharian.sg`;
  const metaDescription =
    article.seoFields?.metaDescription &&
    article.seoFields.metaDescription !== ""
      ? article.seoFields.metaDescription
      : `Berita - ${getFirstParagraph(article.articleBody)} Baca lebih lanjut di BeritaHarian.sg`;
  const metaRobots =
    article.seoFields?.metaRobots && article.seoFields.metaRobots == 1
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const gaData = {
    title: article.title,
    level2: level2,
    chapter1: getChapter1(article),
    contentcat: getContentCat(article.contentAccess),
    section: level2,
    contenttype: "1",
    pagination: pageNumber,
    author: getAuthorsName(article.authors || []),
    cue_articleid: article.id,
    articleid: article.id,
    keyword: keywords,
    pubdate: formatDate(article.datetime, "yyyy-MM-dd HH:mm:ss"),
    content_publication_date_utc: article.datetime,
    content_update_date_utc: article.dateEdited,
  } as GADataProps;

  const metaTags = {
    title: metaTitle,
    description: metaDescription,
    slug: article.urlPath,
    ogType: "Article",
    ogTitle: article.ogFields?.ogTitle || metaTitle,
    ogDescription: article.ogFields?.ogDescription || metaDescription,
    ogImage: ogImage,
    keywords: keywords.split(","),
    robotsContent: metaRobots,
    customCanonicalUrl: article.seoFields?.canonicalUrl,
    twitterCardContent: "summary",
    image: image,
    isArticlePage: true,
    publishedTime: formatDate(article.datetime, dateFormatWithTz),
    modifiedTime: formatDate(article.dateEdited, dateFormatWithTz),
    jsonLd: getArticleJSONLD(article),
    gsChannels,
  } as MetaTagsProps;

  return {
    gaData: gaData,
    metaTags: metaTags,
  };
}

export function getFirstParagraph(
  articleBody: ProcessedArticleBody | null,
): string {
  if (!articleBody) return "";
  return (
    articleBody.find((element) => element.type === "paragraph")?.value || ""
  );
}
